import { get, post } from "@/services/axiosUitils";

export interface InterventionCurvePoint {
  phase?: string;
  time_min: number;
  total_rise: number;
  glucose_mg_dl: number;
  new_meal_rise: number;
  prev_meal_remaining: number;
}

export interface InterventionResponse {
  meal_id: string;
  /** Meal label from intervention calculate API */
  meal_name?: string;
  /** Nutrition totals when backend includes them */
  meal_totals?: {
    calorie_content_kcal?: number;
    carbohydrate_content_g?: number;
    GI_estimate?: number;
  };
  meal_items?: Array<{
    name?: string;
    calorie_content_kcal?: number;
    carbohydrate_content_g?: number;
    GI_estimate?: number;
  }>;
  /** Top-level nutrition fields (actual API response format) */
  calorie_content_kcal?: number;
  carbohydrate_content_g?: number;
  GI_estimate?: number;
  /** Flat aliases some APIs use */
  total_calories?: number;
  total_carbohydrates_g?: number;
  gi_estimate?: number;
  /** BBS score from intervention/calculate */
  bbs_score?: number | null;
  /** BBS label from intervention/calculate */
  bbs_label?: string | null;
  /** false = user has NOT yet started this intervention (action window still open) */
  intervention_started?: boolean;
  intervention_available?: boolean;
  /** e.g. barcode_no_glucose_spike when walk tips are unavailable */
  skip_reason?: string | null;
  now_glucose?: number;
  original_peak?: number;
  new_peak?: number;
  time_to_peak_min?: number;
  intervention_drop_mg_dl?: number;
  minutes_needed?: number;
  activity?: string;
  activity_key?: string;
  best_window_minutes_remaining?: number;
  effectiveness_pct?: number;
  prescription_text?: string;
  notification_text?: string;
  glucose_forecast?: {
    no_action_curve?: { points: InterventionCurvePoint[]; starting_glucose?: number };
    intervention_curve?: { points: InterventionCurvePoint[]; starting_glucose?: number };
  };
}

/** @deprecated use InterventionResponse */
export type InterventionPhase = InterventionCurvePoint;

export interface MorningBriefInsight {
  id: string;
  icon: string;
  level: "success" | "warning" | "info" | "error";
  text: string;
  sub: string;
}

export interface MorningBriefData {
  title: string;
  date_label: string;
  generated_at: string;
  insights: MorningBriefInsight[];
}

export interface HomePageFourPillars {
  daily_balance: number;
  glucose_score: number;
  pas_score: number;
  microbiome_score: number;
  wms_score: number;
}

export interface HomePageData {
  success: {
    code: string;
    message: string;
  };
  user: string;
  /** Time-of-day line for header, e.g. "Good Afternoon" */
  greeting?: string;
  today_date_local: string;
  /** Whether user is new or returning, and days since signup */
  "User Status"?: {
    is_new_user: boolean;
    days_since_signup: number;
  };
  /** Daily balance + pillar scores from `food-home-page` */
  "4_pillars"?: HomePageFourPillars;
  "Morning Brief"?: MorningBriefData;
  "Today's Nutrition": {
    "Total Calories": number;
    "Total Carbohydrates": number;
    "Total Proteins": number;
    "Total Fats": number;
    "Total Fiber": number;
    "Total Net Carbs": number;
    "Total Sugar": number;
    "Total Saturated Fat": number;
    "Total Trans Fat": number;
  };
  "Today's Activity": {
    "Total Steps": number;
    BPM: number;
    "Calories Burned": number;
    Sleep: number;
    "Active Minutes": number;
    spo2_percent: number;
    breathing_rate: number;
    skin_temp_celsius: number;
    hrv_rmssd: number;
  };
  "Intermittent Fasting": {
    Status: string;
    "Time Remaining": string | null;
    "Fasting Time": number | null;
    "Fasting Time UI": string | null;
    "Time Remaining UI": string | null;
    "Time Started": string;
    "Time Ended": string;
    "Elapsed Time": string | null;
    "Elapsed Time UI": string | null;
    "Elapsed Seconds": number | null;
    "Duration Minutes": number;
    "Duration UI": string;
    "Fasting Goal": string | null;
    "Fasting Goal Hours": number | null;
  };
  "Today's Goals": {
    "Total Calories Goal": number;
    "Total Activity Goal": number;
  };
  "Body Mass Index": {
    BMI: number;
    "Weight kg": number;
    "Height cm": number;
    "Weight lbs": number;
    "Height ft": string;
    "BMI Category": string;
    "BMI Category Color": string;
  };
  "Medication Reminders": Array<{
    "Timing ID": string;
    "Medication ID": string;
    "Medication Name": string;
    "Medication Time": string;
    "Full Time String": string;
    Status: string;
    Frequency: string;
  }>;
  "Weekly Calorie Burned Trend": {
    graph: Array<{ day: string; value: number }>;
    start_date: string;
    end_date: string;
  };
  "Weekly Sleep Trend": {
    graph: Array<{ day: string; value: number }>;
    start_date: string;
    end_date: string;
  };
  "Weekly Calorie Intake Trend": {
    graph: Array<{ day: string; value: number }>;
    start_date: string;
    end_date: string;
  };
  "Gut Health Score": number;
  "Food Score": number;
  "Lifestyle Score": number;
  Classification: string;
  "Food GHI"?: {
    daily_ghi: number;
    label: string;
    provisional: boolean;
    meals_logged: number;
    reasons: any[];
  };
  "Bite Balance Score"?: {
    daily_bbs: number;
    label: string;
    provisional: boolean;
    meals_logged: number;
    reasons: any[];
  };
  "Health Insights": {
    carb_fiber_ratio_daily: {
      value: number;
      label: string;
      score?: number;
    };
    bad_fat_ratio_daily: {
      value: number;
      label: string;
      ratio_display?: string;
      score?: number;
    };
    sugar_ratio_daily: {
      value: number;
      label: string;
      ratio_display?: string;
      score?: number;
    };
    protein_fiber_ratio_daily: {
      value: number;
      label: string;
      score?: number;
    };
  };
  glucose_spike?: {
    spike_category: string;
    spike_band: string;
    spike_range: {
      low: number;
      high: number;
    };
    spike_level: number;
    color: string;
    emoji: string;
    title: string;
    description: string;
    ui_message: string;
    baseline_glucose?: number;
    glucose_curve?: {
      points: Array<{ time_min: number; glucose_mg_dl: number }>;
    };
    predicted_spike_mg_dl: number;
    this_meal_spike_mg_dl: number;
    is_cumulative: boolean;
    previous_meal_remaining_mg_dl: number;
    previous_meal_elapsed_min: number | null;
    cumulative_note: string | null;
    confidence: string;
    confidence_margin_mg_dl: number;
    time_to_peak_min: number;
    recovery_time_min: number;
    recommendations: string[];
  };
  "Lifestyle Synergy"?: {
    lifestyle_score: number;
    reasons: any[];
  };
  "Glucose Spikes"?: {
    date_local: string;
    total_meals_count: number;
    meals: Array<{
      meal_id: string;
      meal_type: string;
      meal_time: string;
      meal_time_display: string;
      title?: string;
      this_meal_spike_mg_dl: number;
      previous_meal_remaining_mg_dl: number;
      is_cumulative: boolean;
      spike_category: string;
      spike_band: string;
      title: string;
      color: string;
      emoji: string;
    }>;
    daily_summary: {
      date_local: string;
      meals_with_spike_saved_count: number;
      meals_with_numeric_this_meal_spike: number;
      avg_this_meal_spike_mg_dl: number;
      max_this_meal_spike_mg_dl: number;
      min_this_meal_spike_mg_dl: number;
    };
    meal_type_counts: Record<string, number>;
    meal_type_summaries: Array<{
      meal_type: string;
      meals_count: number;
      avg_this_meal_spike_mg_dl: number | null;
      max_this_meal_spike_mg_dl: number | null;
      min_this_meal_spike_mg_dl: number | null;
    }>;
  };
  /** Personal streak from API */
  "Streak"?: {
    current_days: number;
    logged_today: boolean;
  };
  /** Present when viewing home data in a family group context */
  group_streak?: {
    days: number;
    label: string;
    all_members_logged_today: boolean;
    requirement: string;
    /** Leaderboard rank of this group — optional, added when backend supports it */
    rank?: number;
    /** Total groups in leaderboard — optional */
    total_groups?: number;
  };
  /** Family / circle groups payload from the API when applicable */
  "Family Health"?: {
    groups?: Array<{
      id: string;
      name?: string;
      is_selected?: boolean;
      members?: Array<{
        user_id: string;
        display_name: string;
        initial: string;
        relationship: string | null;
        is_current_user: boolean;
        is_approve: boolean;
        token: string | null;
        approval_tokens: string[];
        meals_logged: number;
        status_message: string;
        bbs_score: number | null;
        bbs_label: string | null;
        bbs_band: string | null;
        glucose_band: string | null;
      }>;
      summary?: {
        members_total: number;
        headcount_with_pending: number;
      };
    }>;
  };
  /** Powering This Prediction — contextual signals powering the glucose prediction */
  "Powering This Prediction"?: Array<{
    title: string;
    value: string;
    meta?: string;
  }>;
  /** Plate Optimisation — eating order tip from the API */
  plate_optimisation?: {
    tip_type: string;
    headline: string;
    subtext: string;
    stats: {
      peak_reduction_pct: number;
      food_change: number;
      effort_min: number;
    };
    steps: Array<{
      order: number;
      dot_type: "green" | "amber" | "gray";
      label: string;
      sub: string;
    }>;
    triggered_by: string;
  };
  /** Meal Patterns — multi-meal historical pattern analysis from the API */
  "Meal Patterns"?: {
    has_pattern: boolean;
    triggered_by: string;
    pattern_summary: string;
    protocol_title: string;
    protocol_description: string;
    avg_spike_without: number;
    avg_spike_with: number;
    typical_savings_mg_dl: number;
    meals_analyzed: number;
    confidence: "high" | "medium" | "low";
    steps: Array<{
      order: number;
      label: string;
      dot_type: "green" | "amber" | "gray";
    }>;
  };
  /** Weight Management Score from the API */
  "Weight Management"?: {
    wms_score: number;
    predicted_kg_4wk: number;
    predicted_change_display?: string;
    ci80_lo: number;
    ci80_hi: number;
    prob_loss_half_kg: number;
    prob_loss_1_kg: number;
    levers: {
      energy_balance: number;
      meal_timing: number;
      protein: number;
      sleep: number;
      meal_quality: number;
      gut_health: number;
    };
    primary_lever: string;
    primary_lever_key: string;
    prescription_text: string;
    score_band: string;
    score_message: string;
    prediction_tone: string;
    lever_tips: Record<string, string>;
    body_composition_score: number;
    tdee_kcal: number;
    energy_balance_kcal: number;
    meta: {
      meals_in_window: number;
      gis_from_home_snapshots: boolean;
    };
  };
}

class HomeService {
  private static instance: HomeService;

  private constructor() { }

  public static getInstance(): HomeService {
    if (!HomeService.instance) {
      HomeService.instance = new HomeService();
    }
    return HomeService.instance;
  }

  /**
   * Get home page data including nutrition, activity, BMI, and weekly trends
   */
  async getHomePageData(date?: string, groupId?: string, config?: any): Promise<HomePageData> {
    let url = date
      ? `/food_scanner/food-home-page/?date=${date}`
      : "/food_scanner/food-home-page/";
    
    // Add group_id filter if provided
    if (groupId) {
      url += date ? `&group_id=${groupId}` : `?group_id=${groupId}`;
    }
    
    const response = await get<HomePageData>(url, config);
    return response;
  }

  /**
   * Start intermittent fasting
   */
  async startFasting(): Promise<any> {
    const response = await post("/food_scanner/intermittent-fasting/", {
      action: "start",
    });
    return response;
  }

  /**
   * Stop/End intermittent fasting
   */
  async stopFasting(): Promise<any> {
    const response = await post("/food_scanner/intermittent-fasting/", {
      action: "end",
    });
    return response;
  }

  /**
   * Calculate glucose intervention curve for a given meal + activity
   */
  async calculateIntervention(mealId: string, activityKey: string, interventionStarted?: boolean): Promise<InterventionResponse | null> {
    const body: Record<string, unknown> = { meal_id: mealId, activity_key: activityKey };
    if (interventionStarted !== undefined) {
      body.intervention_started = interventionStarted;
    }
    const response = await post<InterventionResponse>(
      "/food_scanner/glucose-readings/intervention/calculate/",
      body,
      { showNotification: false }
    );
    return response ?? null;
  }
}

export default HomeService.getInstance();
